import { useState } from "react";
import {
  House,
  MagnifyingGlass,
  ChartBar,
  Trophy,
  User,
  Bell,
  CaretRight,
  List,
  Books,
  MapTrifold,
  Gear,
  Star,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Mascot } from "../styleguide/Mascot";

/* ----------------------------------- Top Header ---------------------------------- */
export function TopHeader() {
  return (
    <header className="flex items-center justify-between gap-3 bg-card px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent">
          <Mascot size={28} animated={false} />
        </div>
        <div>
          <p className="font-display text-base font-extrabold leading-none">Hi, Maya!</p>
          <p className="mt-1 text-xs font-bold text-muted-foreground">Year 3 · Let&apos;s learn</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary/25 px-3 py-1.5 text-sm font-bold text-secondary-ink">
          <Star size={15} weight="fill" /> 240
        </span>
        <button aria-label="Notifications" className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-muted text-foreground">
          <Bell size={20} weight="bold" />
          <span className="absolute right-2.5 top-2.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-card" />
        </button>
      </div>
    </header>
  );
}

/* ------------------------------ Mobile Bottom Nav -------------------------------- */
export function BottomNav({ active = "home" }) {
  const items = [
    { id: "home", label: "Home", icon: House },
    { id: "learn", label: "Learn", icon: Books },
    { id: "rewards", label: "Rewards", icon: Trophy },
    { id: "me", label: "Me", icon: User },
  ];
  return (
    <nav className="flex items-stretch justify-around border-t border-border bg-card px-2 py-2" aria-label="Primary">
      {items.map(({ id, label, icon: Icon }) => {
        const on = id === active;
        return (
          <button
            key={id}
            data-testid={`bottomnav-${id}`}
            className={cn(
              "flex min-h-[52px] flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-1.5 transition-colors",
              on ? "text-primary-strong" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span className={cn("flex h-9 w-9 items-center justify-center rounded-xl transition-colors", on && "bg-primary/12")}>
              <Icon size={22} weight={on ? "fill" : "bold"} />
            </span>
            <span className="text-xs font-bold">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/* ------------------------------ Desktop Sidebar ---------------------------------- */
export function DemoSidebar({ active = "home" }) {
  const items = [
    { id: "home", label: "Home", icon: House },
    { id: "subjects", label: "Subjects", icon: Books },
    { id: "path", label: "My Path", icon: MapTrifold },
    { id: "rewards", label: "Rewards", icon: Trophy },
    { id: "progress", label: "Progress", icon: ChartBar },
    { id: "settings", label: "Settings", icon: Gear },
  ];
  return (
    <aside className="flex w-60 flex-col gap-1 bg-card p-4">
      <div className="mb-4 flex items-center gap-2.5 px-1">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-accent">
          <Mascot size={26} animated={false} />
        </div>
        <p className="font-display text-lg font-extrabold">Bloom</p>
      </div>
      {items.map(({ id, label, icon: Icon }) => {
        const on = id === active;
        return (
          <button
            key={id}
            className={cn(
              "flex min-h-[48px] items-center gap-3 rounded-2xl px-3 text-base font-bold transition-colors",
              on ? "bg-primary/12 text-primary-strong" : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <Icon size={20} weight={on ? "fill" : "bold"} />
            {label}
          </button>
        );
      })}
    </aside>
  );
}

/* --------------------------------- Breadcrumbs ----------------------------------- */
export function Breadcrumbs({ items = [] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm font-bold">
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <span key={item} className="flex items-center gap-1.5">
            <span className={cn(last ? "text-foreground" : "text-muted-foreground", i === 0 && "flex items-center gap-1.5")}>
              {i === 0 && <House size={16} weight="fill" className="text-primary" />}
              {item}
            </span>
            {!last && <CaretRight size={14} weight="bold" className="text-muted-foreground/60" />}
          </span>
        );
      })}
    </nav>
  );
}

/* ------------------------------------ Tabs --------------------------------------- */
export function Tabs({ tabs = [], defaultTab }) {
  const [active, setActive] = useState(defaultTab || tabs[0]?.id);
  return (
    <div>
      <div role="tablist" aria-label="Tabs" className="inline-flex flex-wrap gap-1 rounded-full bg-muted p-1.5">
        {tabs.map((t) => {
          const on = t.id === active;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={on}
              onClick={() => setActive(t.id)}
              data-testid={`tab-${t.id}`}
              className={cn(
                "min-h-[44px] rounded-full px-5 text-sm font-bold transition-all duration-200",
                on ? "bg-card text-primary-strong shadow-soft" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div className="mt-4 rounded-2xl bg-muted/40 p-5 font-medium text-muted-foreground">
        {tabs.find((t) => t.id === active)?.content}
      </div>
    </div>
  );
}

/* -------------------------------- Section Header --------------------------------- */
export function SectionHeader({ icon: Icon = List, title, subtitle, actionLabel = "See all" }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        {Icon && (
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/12 text-primary-strong">
            <Icon size={22} weight="fill" />
          </span>
        )}
        <div>
          <h3 className="font-display text-xl font-extrabold leading-tight">{title}</h3>
          {subtitle && <p className="text-sm font-semibold text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <button className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-bold text-primary-strong hover:bg-primary/10">
        {actionLabel} <CaretRight size={15} weight="bold" />
      </button>
    </div>
  );
}
